import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, TextField, Alert, Box, Paper,
  FormControl, InputLabel, Select, MenuItem, Chip,
  OutlinedInput, SelectChangeEvent,
} from '@mui/material';
import { Send, VolumeUp } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface Station {
  id: number;
  name: string;
}

interface Unit {
  id: number;
  name: string;
  unit_type: string;
}

const ALERT_TYPES = [
  { value: 'dispatch', label: 'Dispatch' },
  { value: 'information', label: 'Information' },
  { value: 'equipment_swap', label: 'Equipment Swap' },
  { value: 'hospital_diversion', label: 'Hospital Diversion' },
  { value: 'weather', label: 'Weather' },
  { value: 'test', label: 'Test' },
  { value: 'custom', label: 'Custom' },
];

const PRIORITIES = [
  { value: 1, label: '1 - Emergency' },
  { value: 2, label: '2 - High' },
  { value: 3, label: '3 - Normal' },
  { value: 4, label: '4 - Low' },
  { value: 5, label: '5 - Info Only' },
];

export default function ManualAlertPage() {
  const { tenantId } = useAuth();
  const [stations, setStations] = useState<Station[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedStationIds, setSelectedStationIds] = useState<number[]>([]);
  const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>([]);
  const [alertType, setAlertType] = useState('information');
  const [alertText, setAlertText] = useState('');
  const [priority, setPriority] = useState(3);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadOptions = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [stationsRes, unitsRes] = await Promise.all([
        api.get(`/api/tenants/${tenantId}/stations`),
        api.get(`/api/tenants/${tenantId}/units`),
      ]);
      setStations(stationsRes.data);
      setUnits(unitsRes.data);
    } catch {
      // May not have stations/units yet
    }
  }, [tenantId]);

  useEffect(() => { loadOptions(); }, [loadOptions]);

  const handleStationChange = (event: SelectChangeEvent<number[]>) => {
    const val = event.target.value;
    setSelectedStationIds(typeof val === 'string' ? [] : val);
  };

  const handleUnitChange = (event: SelectChangeEvent<number[]>) => {
    const val = event.target.value;
    setSelectedUnitIds(typeof val === 'string' ? [] : val);
  };

  const handlePreview = async () => {
    if (!tenantId || !alertText) return;
    setPreviewLoading(true);
    try {
      const res = await api.post(
        `/api/tenants/${tenantId}/pronunciation/preview-tts`,
        { text: alertText, use_dictionary: true },
        { responseType: 'blob' }
      );
      const audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      audio.play();
      audio.onended = () => URL.revokeObjectURL(audioUrl);
    } catch {
      setError('TTS preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!tenantId) return;
    if (selectedStationIds.length === 0) {
      setError('Select at least one station');
      return;
    }
    if (!alertText.trim()) {
      setError('Enter alert text');
      return;
    }

    if (!window.confirm(`Send this ${alertType} alert to ${selectedStationIds.length} station(s)?`)) return;

    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post(`/api/tenants/${tenantId}/alerts/manual`, {
        station_ids: selectedStationIds,
        unit_ids: selectedUnitIds,
        alert_type: alertType,
        alert_text: alertText,
        priority,
      });
      setSuccess(`Alert sent successfully (ID: ${res.data.alert_id})`);
      setAlertText('');
      setSelectedStationIds([]);
      setSelectedUnitIds([]);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send alert');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>Manual Alert</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create and send alerts manually. Useful for equipment swaps, hospital diversions, weather alerts, or as a backup during CAD outages.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ p: 3, maxWidth: 700 }}>
        <FormControl fullWidth margin="dense">
          <InputLabel>Stations *</InputLabel>
          <Select
            multiple
            value={selectedStationIds}
            onChange={handleStationChange}
            input={<OutlinedInput label="Stations *" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map(id => {
                  const s = stations.find(st => st.id === id);
                  return <Chip key={id} size="small" label={s?.name || id} />;
                })}
              </Box>
            )}
          >
            {stations.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
            {stations.length === 0 && <MenuItem disabled>No stations configured</MenuItem>}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense">
          <InputLabel>Units (optional)</InputLabel>
          <Select
            multiple
            value={selectedUnitIds}
            onChange={handleUnitChange}
            input={<OutlinedInput label="Units (optional)" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map(id => {
                  const u = units.find(ut => ut.id === id);
                  return <Chip key={id} size="small" label={u?.name || id} />;
                })}
              </Box>
            )}
          >
            {units.map(u => (
              <MenuItem key={u.id} value={u.id}>{u.name} ({u.unit_type})</MenuItem>
            ))}
            {units.length === 0 && <MenuItem disabled>No units configured</MenuItem>}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense">
          <InputLabel>Alert Type</InputLabel>
          <Select value={alertType} label="Alert Type" onChange={e => setAlertType(e.target.value)}>
            {ALERT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense">
          <InputLabel>Priority</InputLabel>
          <Select value={priority} label="Priority" onChange={e => setPriority(e.target.value as number)}>
            {PRIORITIES.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField
          label="Alert Text *"
          fullWidth
          margin="dense"
          multiline
          rows={4}
          value={alertText}
          onChange={e => setAlertText(e.target.value)}
          placeholder="Enter the alert message to be spoken via TTS..."
          helperText="This text will be announced via TTS at the selected stations"
        />

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<VolumeUp />}
            onClick={handlePreview}
            disabled={previewLoading || !alertText}
          >
            {previewLoading ? 'Generating...' : 'Preview TTS'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Send />}
            onClick={handleSend}
            disabled={sending || selectedStationIds.length === 0 || !alertText.trim()}
          >
            {sending ? 'Sending...' : 'Send Alert'}
          </Button>
        </Box>
      </Paper>
    </>
  );
}
